import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency - uses parentheses for negative values (accounting format)
export function formatCurrency(value, compact = false) {
  if (value === undefined || value === null) return '';
  const absVal = Math.abs(value);
  // Any value that rounds to whole-dollar zero (e.g. -$0.40 or +$0.49) renders
  // as "$0" — never "($0)". This kills the "($0)" negative-zero artifact that
  // showed up on CF Net Cash from Financing Activities when the section
  // subtotal floated by sub-cents below zero.
  if (!compact && absVal < 0.5) return '$0';
  const isNegative = value < 0;

  if (compact && absVal >= 1000000) {
    const formatted = `$${(absVal / 1000000).toFixed(1)}M`;
    return isNegative ? `(${formatted})` : formatted;
  }
  if (compact && absVal >= 1000) {
    const formatted = `$${(absVal / 1000).toFixed(0)}K`;
    return isNegative ? `(${formatted})` : formatted;
  }
  // Compact path may also render abs<1 as "$0" — same normalization.
  if (compact && absVal < 0.5) return '$0';

  const formatted = `$${absVal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  return isNegative ? `(${formatted})` : formatted;
}

// Determine if a change is "good" or "bad" for coloring
// type: 'income' (up=good), 'expense' (down=good), 'asset' (up=good), 'liability' (down=good)
export function getChangeColor(changeValue, type) {
  if (changeValue === 0) return 'inherit';
  
  const isPositiveChange = changeValue > 0;
  
  switch (type) {
    case 'income':
    case 'asset':
      // For income/assets: positive change (increase) is good (green)
      return isPositiveChange ? '#16A34A' : '#DC2626';
    case 'expense':
    case 'liability':
      // For expenses/liabilities: negative change (decrease) is good (green)
      return isPositiveChange ? '#DC2626' : '#16A34A';
    default:
      return 'inherit';
  }
}

// Format percentage
export function formatPercent(value, total) {
  if (!total || total === 0) return '0.0%';
  return `${((value / total) * 100).toFixed(1)}%`;
}

// Get account by ID
export function getAccountById(accounts, id) {
  return accounts.find(a => a.id === id);
}

// Sum account balances
export function sumAccounts(accounts, ids) {
  return ids.reduce((sum, id) => {
    const acc = accounts.find(a => a.id === id);
    return sum + (acc ? acc.balance : 0);
  }, 0);
}

// Sum prior-year balances (from QBO prior-period Trial Balance)
export function sumAccountsPrior(accounts, ids) {
  return ids.reduce((sum, id) => {
    const acc = accounts.find(a => a.id === id);
    return sum + (acc ? (acc.prior_balance || 0) : 0);
  }, 0);
}

// Brand colors
export const COLORS = {
  primary: '#0082B0',
  primaryHover: '#006A8F',
  primaryLight: '#E0F2F7',
  dark: '#0F1923',
  mid: '#1E2D3D',
  subtle: '#2E4156',
  muted: '#8BA3B5',
  off: '#F4F7F9',
  bg: '#F0F5F8',
  card: '#FFFFFF',
  border: '#E2EBF0',
  success: '#16A34A',
  error: '#DC2626',
  warning: '#D97706',
};

// Store auth data
export function setAuthData(token, user) {
  localStorage.setItem('smj_token', token);
  localStorage.setItem('smj_user', JSON.stringify(user));
}

export function getAuthData() {
  const token = localStorage.getItem('smj_token');
  const userStr = localStorage.getItem('smj_user');
  return {
    token,
    user: userStr ? JSON.parse(userStr) : null,
  };
}

export function clearAuthData() {
  localStorage.removeItem('smj_token');
  localStorage.removeItem('smj_user');
}

export function isAuthenticated() {
  return !!localStorage.getItem('smj_token');
}
